import React from 'react';
import { tUi } from '../utils/uiText';

const emptyTerm = () => ({
  source: '',
  target: '',
  do_not_translate: false,
  notes: '',
});

export default function GlossaryPanel({
  uiLanguage,
  terms,
  userLanguageLabel,
  clientLanguageLabel,
  loading,
  saving,
  translating,
  readOnly = false,
  onSave,
  onRefresh,
  onTranslateToClient,
}) {
  const [draftTerms, setDraftTerms] = React.useState([]);

  React.useEffect(() => {
    setDraftTerms(
      (terms || []).map((term) => ({
        source: term.source || '',
        target: term.target || '',
        do_not_translate: Boolean(term.do_not_translate),
        notes: term.notes || '',
      })),
    );
  }, [terms]);

  const updateTerm = (index, patch) => {
    setDraftTerms((current) =>
      current.map((term, termIndex) => (termIndex === index ? { ...term, ...patch } : term)),
    );
  };

  const addTerm = (preset = {}) => {
    setDraftTerms((current) => [...current, { ...emptyTerm(), ...preset }]);
  };

  const removeTerm = (index) => {
    setDraftTerms((current) => current.filter((_, termIndex) => termIndex !== index));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const payload = draftTerms
      .map((term) => ({
        source: term.source.trim(),
        target: term.do_not_translate ? term.source.trim() : term.target.trim(),
        do_not_translate: Boolean(term.do_not_translate),
        notes: term.notes.trim(),
      }))
      .filter((term) => term.source);
    await onSave(payload);
  };

  const handleTranslate = async () => {
    await onSave(
      draftTerms
        .map((term) => ({
          source: term.source.trim(),
          target: term.target.trim(),
          do_not_translate: Boolean(term.do_not_translate),
          notes: term.notes.trim(),
        }))
        .filter((term) => term.source),
    );
    await onTranslateToClient();
  };

  return (
    <div className="mb-4 bg-gray-900 rounded-lg border border-cyan-500/30 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-cyan-400">{tUi(uiLanguage, 'glossaryTitle')}</h3>
          <p className="text-sm text-gray-400">
            {tUi(uiLanguage, 'glossaryStep1Prefix')}{' '}
            <strong className="text-cyan-300">{userLanguageLabel}</strong>{' '}
            {tUi(uiLanguage, 'glossaryStep1Middle')}{' '}
            <strong className="text-cyan-300">{clientLanguageLabel}</strong> or stay locked.
            {' '}
            {tUi(uiLanguage, 'glossaryStep2')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onRefresh()}
          disabled={loading}
          className="px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 border border-cyan-500/30 rounded-lg text-white"
        >
          {tUi(uiLanguage, 'commonRefresh')}
        </button>
      </div>

      {readOnly ? (
        <div className="space-y-2 text-sm text-gray-300">
          {draftTerms.length === 0 ? (
            <p className="text-gray-500">{tUi(uiLanguage, 'glossaryEmptyStatePrefix')}</p>
          ) : (
            draftTerms.map((term, index) => (
              <div key={`glossary-readonly-${index}`} className="border border-cyan-500/20 rounded px-3 py-2">
                <div>{term.source}</div>
                {!term.do_not_translate && term.target && (
                  <div className="text-gray-400 text-xs mt-1">{term.target}</div>
                )}
              </div>
            ))
          )}
          <p className="text-[11px] text-gray-500">Read-only for operator accounts.</p>
        </div>
      ) : (
      <form onSubmit={handleSave} className="space-y-3">
        {draftTerms.length === 0 && (
          <p className="text-sm text-gray-500">
            {tUi(uiLanguage, 'glossaryEmptyStatePrefix')}{' '}
            <em className="text-cyan-300">{tUi(uiLanguage, 'glossaryExampleLocked')}</em>, {tUi(uiLanguage, 'glossaryExampleTranslate')}{' '}
            <em className="text-cyan-300">{tUi(uiLanguage, 'glossaryExampleTranslationPair')}</em>.
          </p>
        )}

        {draftTerms.map((term, index) => (
          <div
            key={`glossary-${index}`}
            className="bg-gray-800 border border-cyan-500/20 rounded-lg p-3 space-y-3"
          >
            <div className="grid gap-3 md:grid-cols-[1.2fr_1fr_1.2fr_auto] items-end">
              <label className="block text-sm text-gray-300">
                <span className="block mb-1 text-xs uppercase tracking-wide text-gray-400">
                  {tUi(uiLanguage, 'glossaryTermLabel', { language: userLanguageLabel })}
                </span>
                <input
                  value={term.source}
                  onChange={(e) => updateTerm(index, { source: e.target.value })}
                  placeholder={tUi(uiLanguage, 'glossaryTermPlaceholder')}
                  className="w-full px-3 py-2 bg-black border border-cyan-500/30 rounded-lg text-white"
                />
              </label>

              <fieldset className="block text-sm text-gray-300">
                <legend className="block mb-1 text-xs uppercase tracking-wide text-gray-400">
                  {tUi(uiLanguage, 'glossaryTranslationRule')}
                </legend>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`policy-${index}`}
                      checked={!term.do_not_translate}
                      onChange={() => updateTerm(index, { do_not_translate: false })}
                    />
                    {tUi(uiLanguage, 'glossaryTranslateToClient')}
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`policy-${index}`}
                      checked={term.do_not_translate}
                      onChange={() => updateTerm(index, { do_not_translate: true, target: term.source })}
                    />
                    {tUi(uiLanguage, 'glossaryKeepAsIs')}
                  </label>
                </div>
              </fieldset>

              <label className="block text-sm text-gray-300">
                <span className="block mb-1 text-xs uppercase tracking-wide text-gray-400">
                  {tUi(uiLanguage, 'glossaryClientTranslationLabel', { language: clientLanguageLabel })}
                </span>
                <input
                  value={term.do_not_translate ? term.source : term.target}
                  onChange={(e) => updateTerm(index, { target: e.target.value })}
                  disabled={term.do_not_translate}
                  placeholder={term.do_not_translate ? tUi(uiLanguage, 'glossaryLockedPlaceholder') : tUi(uiLanguage, 'glossaryGenerateOrEditPlaceholder')}
                  className="w-full px-3 py-2 bg-black border border-cyan-500/30 rounded-lg text-white disabled:opacity-60"
                />
              </label>

              <button
                type="button"
                onClick={() => removeTerm(index)}
                className="px-3 py-2 text-sm bg-red-900/40 hover:bg-red-800/50 text-red-300 rounded-lg"
              >
                {tUi(uiLanguage, 'commonRemove')}
              </button>
            </div>

            <input
              value={term.notes}
              onChange={(e) => updateTerm(index, { notes: e.target.value })}
              placeholder={tUi(uiLanguage, 'glossaryNotesPlaceholder')}
              className="w-full px-3 py-2 bg-black border border-cyan-500/30 rounded-lg text-white"
            />
          </div>
        ))}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => addTerm()}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-cyan-500/30 rounded-lg text-white"
          >
            {tUi(uiLanguage, 'glossaryAddTerm')}
          </button>
          <button
            type="button"
            onClick={() => addTerm({ do_not_translate: true })}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-cyan-500/30 rounded-lg text-white"
          >
            {tUi(uiLanguage, 'glossaryAddLockedName')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 text-black font-semibold rounded-lg"
          >
            {saving ? tUi(uiLanguage, 'commonSaving') : tUi(uiLanguage, 'glossarySave')}
          </button>
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translating || saving || draftTerms.length === 0}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg"
          >
            {translating ? tUi(uiLanguage, 'glossaryTranslating') : tUi(uiLanguage, 'glossaryGenerateClientTranslations', { language: clientLanguageLabel })}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
